import React, { useState, useCallback, useEffect } from 'react';
import { Button, Typography, Box } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import { SnackbarSeverityType } from '../../../enums';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import { FeeFineDetailsData } from '../../../types/paymentScheme';

interface DeletePendingLateFeeDialogProps {
  open: boolean;
  onClose: () => void;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
  onUpdate: () => void;
  selectedFeeFineDetails: FeeFineDetailsData[];
}

const DeletePendingLateFeeDialog: React.FC<DeletePendingLateFeeDialogProps> = ({
  open,
  onClose,
  onShowToast,
  onUpdate,
  selectedFeeFineDetails
}) => {
  const [loading, setLoading] = useState(false);

  const resetState = useCallback(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleDelete = async () => {
    setLoading(true);
    try {
      const payload = selectedFeeFineDetails
        .filter((data) => (data.pendingLateFee || 0) > 0)
        .map((row) => ({
          paymentSchemeId: row._id,
          updatedLateFee:
            (row.paidLateFee || 0) <= (row.lateFee || 0)
              ? Number((row.paidLateFee || 0).toFixed(2))
              : row.lateFee || 0
        }));

      await paymentSchemesApis.updatePendingLateFees(payload);
      onShowToast(
        'Pending late fees updated successfully!',
        SnackbarSeverityType.success
      );
      onUpdate();
      onClose();
      resetState();
    } catch (error: any) {
      console.error('Error updating pending late fees:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to update pending late fees.',
        SnackbarSeverityType.error
      );
    } finally {
      setLoading(false);
    }
  };

  const dialogActions = (
    <>
      <Button onClick={onClose} sx={{ textTransform: 'none' }}>
        Cancel
      </Button>
      <Button
        variant='contained'
        color='error'
        onClick={handleDelete}
        sx={{ textTransform: 'none' }}
        disabled={loading}
      >
        Delete
      </Button>
    </>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Delete Pending Late Fees'
      maxWidth='sm'
      actions={dialogActions}
    >
      {loading && <OverlayLoader show={loading} />}
      <Box sx={{ p: 2 }}>
        <Typography variant='body1'>
          Are you sure you want to delete selected pending late fees?
        </Typography>
      </Box>
    </DialogModal>
  );
};

export default DeletePendingLateFeeDialog;
